import { Search } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** One setting as the search sees it: its words, its page and its value in words. */
export interface SearchEntry {
  key: string;
  label: string;
  help: string;
  /** The page's name, also matched ("security" finds the sign-in settings). */
  group: string;
  section: string;
  value: string;
}

const MAX_RESULTS = 8;

/** Lower case without accents, so "securite" finds "Sécurité". */
const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/** The entries matching every word of `query`, best first: label matches before help ones. */
export function matchSettings(entries: readonly SearchEntry[], query: string): SearchEntry[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }
  const scored = entries.flatMap((entry) => {
    const label = fold(entry.label);
    const all = `${label} ${fold(entry.help)} ${fold(entry.group)} ${fold(entry.section)}`;
    if (!words.every((w) => all.includes(w))) {
      return [];
    }
    return [{ entry, score: words.filter((w) => label.includes(w)).length }];
  });
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map((s) => s.entry);
}

/**
 * The settings search: an ARIA 1.2 combobox over every setting of the level shown (labels,
 * help and page names, en or fr as displayed). Arrow keys move, Enter or a click picks (the
 * setting's page opens with focus on it), Escape closes the list, then clears the field.
 */
export function SettingsSearch(props: {
  entries: readonly SearchEntry[];
  onPick: (key: string) => void;
  /** The field's label and placeholder, when not the backoffice settings' own. */
  label?: string;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const results = useMemo(() => matchSettings(props.entries, text), [props.entries, text]);
  const expanded = open && results.length > 0;
  // The results can shrink under the index (another level's settings): it stays on one.
  const current = Math.min(active, Math.max(results.length - 1, 0));
  const optionId = (i: number) => `${id}-option-${i}`;
  const listId = `${id}-list`;
  const statusId = `${id}-status`;

  const choose = (entry: SearchEntry | undefined) => {
    if (!entry) {
      return;
    }
    setText("");
    setOpen(false);
    props.onPick(entry.key);
  };
  const move = (step: 1 | -1) => {
    if (results.length === 0) {
      return;
    }
    // Reopening a closed list shows the current option before moving on.
    if (!expanded) {
      setOpen(true);
      return;
    }
    const next = (current + step + results.length) % results.length;
    setActive(next);
    document.getElementById(optionId(next))?.scrollIntoView?.({ block: "nearest" });
  };

  const status =
    text.trim() === ""
      ? ""
      : results.length === 0
        ? t("adminSettings.search.none")
        : t("adminSettings.search.found", { count: results.length });

  return (
    <div className="relative flex flex-col gap-1.5">
      <Label htmlFor={`${id}-input`}>{props.label ?? t("adminSettings.search.label")}</Label>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
          strokeWidth={1.75}
        />
        <Input
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={expanded ? optionId(current) : undefined}
          aria-describedby={statusId}
          autoComplete="off"
          spellCheck={false}
          placeholder={props.placeholder ?? t("adminSettings.search.placeholder")}
          className="pl-9"
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              move(e.key === "ArrowDown" ? 1 : -1);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (expanded) {
                choose(results[current]);
              }
            } else if (e.key === "Escape") {
              // First press closes the list, the next one clears the field.
              e.preventDefault();
              if (expanded) {
                setOpen(false);
              } else {
                setText("");
              }
            }
          }}
          onBlur={() => setOpen(false)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {expanded ? (
        <div
          id={listId}
          role="listbox"
          aria-label={t("adminSettings.search.results")}
          // Scrolling the list must not blur the input and close it.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute top-full right-0 left-0 z-20 mt-1 max-h-80 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface-overlay shadow-md"
        >
          {results.map((entry, i) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard (aria-activedescendant).
            <div
              key={entry.key}
              id={optionId(i)}
              role="option"
              tabIndex={-1}
              aria-selected={i === current}
              className={cn(
                "flex cursor-pointer flex-col gap-0.5 px-3 py-2 text-sm transition-colors duration-(--duration-fast)",
                i === current && "bg-surface-selected",
              )}
              onMouseMove={() => {
                if (current !== i) {
                  setActive(i);
                }
              }}
              onClick={() => choose(entry)}
            >
              <span className="truncate font-medium text-fg">{entry.label}</span>
              <span className="truncate text-xs text-fg-muted">
                {entry.group} · {entry.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <p id={statusId} aria-live="polite" className="sr-only">
        {status}
      </p>
    </div>
  );
}
